import {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from 'react';

// Light / dark / system. "system" removes data-theme so design/tokens.css follows the OS; the
// other two set it. index.html applies the stored value before first paint to avoid a flash.
export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'theme';

const readInitial = (): ThemeMode => {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored === 'light' || stored === 'dark') return stored;
    } catch {
        // ignore
    }
    return 'system';
};

const ThemeContext = createContext<{mode: ThemeMode; setMode: (mode: ThemeMode) => void}>({
    mode: 'system',
    setMode: () => {}
});

export const ThemeProvider = ({children}: {children: ReactNode}) => {
    const [mode, setModeState] = useState<ThemeMode>(readInitial);

    useEffect(() => {
        const root = document.documentElement;
        if (mode === 'system') root.removeAttribute('data-theme');
        else root.setAttribute('data-theme', mode);
        try {
            if (mode === 'system') window.localStorage.removeItem(STORAGE_KEY);
            else window.localStorage.setItem(STORAGE_KEY, mode);
        } catch {
            // ignore
        }
    }, [mode]);

    const setMode = useCallback((next: ThemeMode) => setModeState(next), []);
    const value = useMemo(() => ({mode, setMode}), [mode, setMode]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => useContext(ThemeContext);
